import { setContext } from "@apollo/client/link/context";
import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  NormalizedCacheObject,
  createHttpLink,
} from "@apollo/client";
import { ApiLegacyName } from "../constants/apollo";
import { getToken } from "@onmo/auth";

const ONMO_CONFIG = JSON.parse(import.meta.env.REACT_APP_ONMO_CFG || "{}");
const httpLink = createHttpLink({
  uri: ONMO_CONFIG?.api?.httpApolloUrl,
  includeExtensions: true,
});

const legacyHttpLink = createHttpLink({
  uri: ONMO_CONFIG?.api?.legacyHttpApolloUrl,
  includeExtensions: true,
});

const primaryLegacyHttpLink = createHttpLink({
  uri: ONMO_CONFIG?.api?.primaryLegacyHttpApolloUrl,
  includeExtensions: true,
});

const isLegacyApi = (api: string) => {
  return (Object.values(ApiLegacyName) as string[]).includes(api);
};

const authLink = setContext(async (_, { headers, api }) => {
  const accessToken = await getToken();
  return {
    headers: {
      ...headers,
      authorization: !isLegacyApi(api)
        ? `Bearer ${accessToken}`
        : accessToken || "",
    },
  };
});
let client: ApolloClient<NormalizedCacheObject> | null = null;

/**
 * Apollo client singleton supporting secured websocket and https transport.
 * Use for application
 */
export const getInstance = (): ApolloClient<NormalizedCacheObject> => {
  if (client) return client;

  client = new ApolloClient({
    queryDeduplication: false,
    link: ApolloLink.split(
      (operation) => !isLegacyApi(operation.getContext().api),
      ApolloLink.from([authLink, httpLink]),
      ApolloLink.split(
        (operation) =>
          operation.getContext().api === ApiLegacyName.PrimaryLegacy,
        ApolloLink.from([authLink, primaryLegacyHttpLink]),
        ApolloLink.from([authLink, legacyHttpLink]),
      ),
    ),
    cache: new InMemoryCache({}),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-and-network",
        errorPolicy: "ignore",
      },
      query: {
        fetchPolicy: "network-only",
        errorPolicy: "all",
      },
    },
  });

  return client;
};

const apollo = {
  getInstance,
};

export default apollo;
